import { CHAT_READ_CHANGED_EVENT, ChatReader, getChatReaders } from '@/lib/api/atendimento/chat'
import { format, isToday, isYesterday } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdDoneAll } from 'react-icons/md'

interface ChatReadersProps {
    chatId: number
}

function formatReadAt(value: string): string {
    const date = new Date(value)
    if (isToday(date)) return `hoje às ${format(date, 'HH:mm')}`
    if (isYesterday(date)) return `ontem às ${format(date, 'HH:mm')}`
    return format(date, "dd/MM 'às' HH:mm")
}

/**
 * "Visto por" no cabeçalho da conversa.
 *
 * A leitura é por pessoa: abrir a conversa não a marca como lida para os colegas. Isto
 * mostra quem já viu, separando quem viu a ÚLTIMA mensagem do contato de quem só viu
 * antes dela — é a diferença entre "alguém está em cima disso" e "alguém passou por aqui
 * semana passada".
 *
 * A lista é buscada ao abrir o painel (é ali que ela importa) e quando a própria pessoa
 * lê a conversa.
 */
export default function ChatReaders({ chatId }: ChatReadersProps) {
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [readers, setReaders] = useState<ChatReader[]>([])
    const [lastInboundAt, setLastInboundAt] = useState<string | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await getChatReaders(chatId)
            if (res.success && res.result) {
                setReaders(res.result.readers)
                setLastInboundAt(res.result.lastInboundAt)
            }
        } finally {
            setLoading(false)
        }
    }, [chatId])

    useEffect(() => {
        setReaders([])
        setLastInboundAt(null)
        load()
    }, [load])

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<{ chatId: number }>).detail
            if (detail?.chatId === chatId) load()
        }
        window.addEventListener(CHAT_READ_CHANGED_EVENT, handler)
        return () => window.removeEventListener(CHAT_READ_CHANGED_EVENT, handler)
    }, [chatId, load])

    useEffect(() => {
        if (!open) return
        const onClickOutside = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', onClickOutside)
        return () => document.removeEventListener('mousedown', onClickOutside)
    }, [open])

    const toggle = () => {
        const next = !open
        setOpen(next)
        if (next) load()
    }

    const lastInbound = lastInboundAt ? new Date(lastInboundAt).getTime() : null
    const sawLatest = readers.filter(
        reader => lastInbound === null || new Date(reader.lastReadAt).getTime() >= lastInbound,
    )
    const sawBefore = readers.filter(reader => !sawLatest.includes(reader))

    const renderGroup = (title: string, items: ChatReader[]) =>
        items.length > 0 && (
            <div className="space-y-1">
                <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-base-content/50">
                    {title}
                </p>
                <ul className="space-y-0.5">
                    {items.map(reader => (
                        <li
                            key={reader.userId}
                            className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-base-200/60"
                        >
                            <span className="truncate text-sm">{reader.name}</span>
                            <span className="flex-shrink-0 text-xs text-base-content/60">
                                {formatReadAt(reader.lastReadAt)}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        )

    return (
        <div ref={containerRef} className="relative flex-shrink-0">
            <button
                type="button"
                onClick={toggle}
                className="btn btn-ghost btn-xs gap-1 text-base-content/70"
                title="Quem da equipe já visualizou esta conversa"
            >
                <MdDoneAll className="text-base" />
                <span className="hidden sm:inline">Visto por</span>
                {sawLatest.length > 0 && (
                    <span className="badge badge-ghost badge-xs">{sawLatest.length}</span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-full z-30 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-base-300 bg-base-100 p-3 shadow-lg">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-sm font-semibold">Visto por</h3>
                        {loading && <span className="loading loading-spinner loading-xs" />}
                    </div>

                    {readers.length === 0 && !loading ? (
                        <p className="px-1 py-2 text-sm text-base-content/60">
                            Ninguém da equipe abriu esta conversa ainda.
                        </p>
                    ) : (
                        <div className="max-h-72 space-y-3 overflow-y-auto">
                            {renderGroup('Viram a última mensagem', sawLatest)}
                            {renderGroup('Viram antes da última mensagem', sawBefore)}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
